import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PROFESSIONAL_CATEGORIES, getCategoryById } from '@/data/categories';
import { WORLD_LANGUAGES, LANGUAGE_NAME_TO_CODE } from '@/data/languages';
import { useProfessionalCategories, getCategoryByIdFromData, normalizeCategory, CATEGORIES_QUERY_KEY, type NormalizedCategory } from './useProfessionalCategories';
import { useEffect } from 'react';

interface ActiveFiltersResponse {
  categories: string[];
  counts: Record<string, number>;
  languages: string[];
  languageCounts: Record<string, number>;
}

export interface CategoryOption {
  value: string;
  label: string;
  icon?: string;
  count: number;
}

export interface LanguageOption {
  value: string;
  label: string;
  count: number;
}

// Static languages fallback - use WORLD_LANGUAGES from data file
const ALL_LANGUAGES = WORLD_LANGUAGES.map(lang => ({
  code: lang.code,
  name: lang.name,
}));

// Shared query for active filters (categories + languages)
function useActiveFilters() {
  return useQuery({
    queryKey: ['active-filters-v2'],
    queryFn: async (): Promise<ActiveFiltersResponse> => {
      const { data, error } = await supabase.functions.invoke('get-active-categories');
      
      if (error) {
        console.error('Error fetching active filters:', error);
        throw error;
      }
      
      return data as ActiveFiltersResponse;
    },
    staleTime: 1000 * 60 * 15, // 15 minutes - categories rarely change
    gcTime: 1000 * 60 * 60, // 1 hour in cache
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
}

// Prefetch function to be called at app init
export function usePrefetchCategories() {
  const queryClient = useQueryClient();
  
  useEffect(() => {
    // Clean old cache keys to prevent poisoned cache
    queryClient.removeQueries({ queryKey: ['professional-categories'] });
    
    // Prefetch active filters on mount
    queryClient.prefetchQuery({
      queryKey: ['active-filters-v2'],
      queryFn: async (): Promise<ActiveFiltersResponse> => {
        const { data, error } = await supabase.functions.invoke('get-active-categories');
        if (error) throw error;
        return data as ActiveFiltersResponse;
      },
      staleTime: 1000 * 60 * 15,
    });
    
    // Prefetch DB categories with NORMALIZED data (same format as useProfessionalCategories)
    queryClient.prefetchQuery({
      queryKey: [CATEGORIES_QUERY_KEY, true],
      queryFn: async (): Promise<NormalizedCategory[]> => {
        const { data, error } = await supabase
          .from('professional_categories')
          .select('*')
          .eq('is_active', true)
          .order('display_order');
        if (error) throw error;
        // CRITICAL: Normalize the data before caching
        return (data || []).map(normalizeCategory);
      },
      staleTime: 1000 * 60 * 30,
    });
  }, [queryClient]);
}

export function useActiveCategories(language: 'it' | 'en' | 'es' = 'it') {
  const filtersQuery = useActiveFilters();
  const categoriesQuery = useProfessionalCategories(true);
  
  // Ensure language is a valid 2-char code with fallback
  const safeLang: 'it' | 'en' | 'es' = ['it', 'en', 'es'].includes(language) ? language : 'it';
  
  const isLoading = filtersQuery.isLoading || categoriesQuery.isLoading;
  const isError = filtersQuery.isError || categoriesQuery.isError;
  
  // Use DB categories if available, otherwise fall back to static
  const dbCategories = categoriesQuery.data || [];
  
  const data = filtersQuery.data?.categories
    ?.map((catId): CategoryOption | null => {
      // First try DB categories
      const dbCat = getCategoryByIdFromData(dbCategories, catId);
      if (dbCat) {
        const localizedName = dbCat[safeLang]?.name ?? dbCat.it?.name ?? catId;
        return {
          value: dbCat.id,
          label: localizedName,
          icon: dbCat.icon,
          count: filtersQuery.data?.counts[catId] || 0,
        };
      }
      // Fallback to static categories
      const staticCat = getCategoryById(catId);
      if (!staticCat) return null;
      const localizedName = staticCat[safeLang]?.name ?? staticCat.it?.name ?? catId;
      return {
        value: staticCat.id,
        label: localizedName,
        icon: staticCat.icon,
        count: filtersQuery.data?.counts[catId] || 0,
      };
    })
    .filter((item): item is CategoryOption => item !== null) || [];

  return {
    ...filtersQuery,
    isLoading,
    isError,
    data,
  };
}

// Fallback to all categories only on error
export function useCategoryOptions(language: 'it' | 'en' | 'es' = 'it') {
  const { data: activeCategories, isLoading, isError } = useActiveCategories(language);
  const { data: dbCategories } = useProfessionalCategories(true);

  // During loading: return empty array (dropdown will show placeholder)
  if (isLoading) {
    return {
      options: [],
      isLoading: true,
      isUsingFallback: false,
    };
  }

  // On error or no data: fallback to DB categories first, then static
  if (isError || !activeCategories?.length) {
    if (dbCategories?.length) {
      return {
        options: dbCategories.map((cat) => ({
          value: cat.id,
          label: cat?.[language]?.name || cat?.it?.name || cat.id,
          icon: cat.icon || '',
          count: 0,
        })),
        isLoading: false,
        isUsingFallback: true,
      };
    }
    return {
      options: PROFESSIONAL_CATEGORIES.map((cat) => ({
        value: cat.id,
        label: cat?.[language]?.name || cat?.it?.name || cat.id,
        icon: cat.icon || '',
        count: 0,
      })),
      isLoading: false,
      isUsingFallback: true,
    };
  }

  return {
    options: activeCategories,
    isLoading: false,
    isUsingFallback: false,
  };
}

// Hook for active languages
export function useActiveLanguages() {
  const query = useActiveFilters();
  
  return {
    ...query,
    data: query.data?.languages
      ?.map((langName): LanguageOption | null => {
        const code = LANGUAGE_NAME_TO_CODE[langName] || langName.toLowerCase().substring(0, 2);
        return {
          value: code,
          label: langName,
          count: query.data?.languageCounts[langName] || 0,
        };
      })
      .filter((item): item is LanguageOption => item !== null) || [],
  };
}

// Wrapper for language options with fallback
export function useLanguageOptions() {
  const { data: activeLanguages, isLoading, isError } = useActiveLanguages();

  // During loading: return empty array (dropdown will show placeholder)
  if (isLoading) {
    return {
      options: [],
      isLoading: true,
      isUsingFallback: false,
    };
  }

  // On error or no data: fallback to all languages
  if (isError || !activeLanguages?.length) {
    return {
      options: ALL_LANGUAGES.map((lang) => ({
        value: lang.code,
        label: lang.name,
        count: 0,
      })),
      isLoading: false,
      isUsingFallback: true,
    };
  }

  return {
    options: activeLanguages,
    isLoading: false,
    isUsingFallback: false,
  };
}
